/**
 * Shared leave-type options for Admin Apply Leave and ESS Leave request forms.
 * When an employeeId is known, only leave types from that employee's leave group(s)
 * are returned (API: GET /attendance-structure/employee-leave-types).
 */
import {
  filterLeaveTypesByGender,
  isFemaleOnlyLeaveType,
  isPaternityLeaveType,
} from "./leaveGenderFilter";

const DEFAULT_EXCLUDE = new Set(["LOP", "WO", "PH", "PR"]);

function getEmployeeGender(employee) {
  return employee?.user?.gender || employee?.gender || "";
}

/**
 * Build display codes from leave types (same rules as Admin Apply Leave).
 * @returns {{ code: string, label: string, id?: number|string }[]}
 */
export function buildLeaveTypeOptions(
  leaveTypes = [],
  {
    employee = null,
    attendanceMapping = null,
    excludeCodes = DEFAULT_EXCLUDE,
  } = {}
) {
  const exclude = excludeCodes instanceof Set ? excludeCodes : new Set(excludeCodes);
  const seen = new Set();
  const combined = [];

  let list = Array.isArray(leaveTypes) ? leaveTypes : [];
  if (employee) {
    list = filterLeaveTypesByGender(list, getEmployeeGender(employee));
  } else {
    list = list.filter((lt) => {
      const applicability = String(lt?.genderApplicability || "all").toLowerCase();
      if (applicability === "female" || applicability === "male") return false;
      if (isFemaleOnlyLeaveType(lt) || isPaternityLeaveType(lt)) return false;
      return true;
    });
  }

  list.forEach((lt) => {
    const isActive = lt.isActive !== undefined ? lt.isActive : true;
    if (isActive !== true) return;

    let code = String(lt.code || lt.shortCode || lt.id || "").toUpperCase();
    if (!code || exclude.has(code)) return;

    const mappedCode =
      attendanceMapping && attendanceMapping[code] ? attendanceMapping[code] : code;
    if (seen.has(mappedCode)) return;
    seen.add(mappedCode);
    combined.push({
      code: mappedCode,
      label: lt.name || lt.leaveTypeName || mappedCode,
      id: lt.id,
    });
  });

  return combined;
}

/**
 * Fetch leave types for forms.
 * @param {number|string|null} employeeId - When set, only types applicable via the
 *   employee's leave group(s). Without employeeId, returns all company leave types
 *   (admin config / bulk flows only).
 * @returns {Promise<{ list: any[], leaveGroups: any[], hasLeaveGroup: boolean|null, message: string|null }>}
 */
export async function fetchLeaveTypesForForms(employeeId = null) {
  try {
    const { attendanceStructureService } = await import("../services/api");
    const id = employeeId != null && employeeId !== "" ? Number(employeeId) : null;

    if (Number.isFinite(id) && id > 0) {
      const res = await attendanceStructureService.getEmployeeLeaveTypes(id);
      const raw = res?.data ?? res;
      return {
        list: Array.isArray(raw) ? raw : [],
        leaveGroups: Array.isArray(res?.leaveGroups) ? res.leaveGroups : [],
        hasLeaveGroup: res?.hasLeaveGroup !== false,
        message: res?.message || null,
      };
    }

    const res = await attendanceStructureService.getLeaveTypes();
    const raw = res?.data ?? res;
    return {
      list: Array.isArray(raw) ? raw : [],
      leaveGroups: [],
      hasLeaveGroup: null,
      message: null,
    };
  } catch (error) {
    return {
      list: [],
      leaveGroups: [],
      hasLeaveGroup: false,
      message: error?.message || "Failed to load leave types",
    };
  }
}

export function defaultLeaveTypeCode(options = []) {
  return options[0]?.code || "";
}
